// Base HTTP client with the existing patterns from util/api.js
import { types as sdkTypes, transit } from '../util/sdkLoader';
import config from '../config';
import Decimal from 'decimal.js';
import axios from 'axios';

export const apiBaseUrl = () => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;
  const isWindowDefined = typeof window !== 'undefined';

  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  return isWindowDefined ? `${window.location.origin}` : process.env.REACT_APP_CANONICAL_ROOT_URL;
};

// Application type handlers for JS SDK.
export const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const includeHeaders = body => {
  const isBodyFormData = body instanceof FormData;
  return !isBodyFormData
    ? {
        headers: {
          'Content-Type': 'application/transit+json',
        },
      }
    : {};
};

export const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: config.sdk.transitVerbose });
};

export const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

export const post = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    ...includeHeaders(body),
    body,
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }

    return res.text();
  });
};

// JSON POST - for endpoints that don't use Transit encoding
export const postJson = (path, body = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  return window.fetch(url, options).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const get = (path, params) => {
  const url = `${apiBaseUrl()}${path}`;
  return axios(url, { params, withCredentials: true });
};

/**
 * Transit-aware GET. Use this when the server returns a Sharetribe SDK
 * response (rich types like UUID, Money, LatLng need to round-trip
 * intact). Server must send `Content-Type: application/transit+json`.
 *
 * Falls back to `application/json` parsing if the server sends plain JSON.
 */
export const getTransit = (path, params) => {
  const qs = params
    ? '?' +
      Object.keys(params)
        .filter(k => params[k] !== undefined && params[k] !== null)
        .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&')
    : '';
  const url = `${apiBaseUrl()}${path}${qs}`;
  return window
    .fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/transit+json' },
    })
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      if (res.status >= 400) {
        return res.json().then(data => {
          const e = Object.assign(new Error(), data);
          e.status = res.status;
          throw e;
        });
      }
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      }
      return res.json();
    });
};

export const put = (path, body) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'PUT',
    credentials: 'include',
    ...includeHeaders(body),
    body,
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }

    return res.text();
  });
};

// JSON PUT - for endpoints that don't use Transit encoding
export const putJson = (path, body = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  };
  return window.fetch(url, options).then(res => {
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    return res.json();
  });
};

export const del = (path) => {
  const url = `${apiBaseUrl()}${path}`;
  const options = {
    method: 'DELETE',
    credentials: 'include',
  };
  return window.fetch(url, options).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }

    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }

    return res.text();
  });
};